import clsx from "clsx"
import { FC, memo, useRef } from "react"
import { CardTextShadowImg } from "@/assets"
import { getCdnAssetUrl } from "@/helpers/getCdnAssetUrl"
import { IRuneType } from "@/pages/Game/ui"
import { runeImageTypeMap } from "@/pages/Game/ui/UserRuneBalance/ui/UserRuneBalanceItem/values"
import { ArcText } from "@/ui/ArcText"
import { OutlinedText } from "@/ui/OutlinedText"
import styles from "./Card.module.scss"
import { CardDescriptionSvg } from "./CardDescriptionSvg"
import { useDescriptionFit } from "./hooks"
import { ICardViewProps } from "./types"
import { getCardViewSkin } from "./values"

// true = SVG text (bypasses Android minimum font size)
// false = HTML <p> with useDescriptionFit
const USE_SVG_DESCRIPTION = true

const CardViewComponent: FC<ICardViewProps> = ({
  data,
  className,
  dataFlipId,
  owner,
  status,
  isShowFrontSide = true,
  isShowBackSide = true,
  isMatchActive,
  isShowActive = true,
  title,
  description,
  skin,
  cardRef,
  contentRef,
  onCardClick,
  onCardTouchEnd,
  cardSide,
  descriptionScale,
  ...props
}) => {
  const cardSkin = getCardViewSkin(skin)
  const runeImg = data?.currency
    ? runeImageTypeMap[data.currency as IRuneType]
    : undefined

  const descriptionRef = useRef<HTMLDivElement | null>(null)

  const { fontSize, lineClamp } = useDescriptionFit(descriptionRef, {
    scale: descriptionScale ?? 1,
    observe: true,
    enabled: isShowFrontSide && !USE_SVG_DESCRIPTION
  })

  return (
    <div
      {...props}
      id={undefined}
      className={clsx(
        "card-item",
        owner,
        status,
        styles.Card,
        styles[`skin_${cardSkin.type}`],
        owner && styles[owner],
        status && styles[status],
        className,
        {
          [styles[cardSide || ""]]: !!cardSide,
          [styles.disabled]: !isShowActive,
          [styles.isEndMatch]: !isMatchActive
        }
      )}
      data-flip-id={dataFlipId}
      onClick={onCardClick}
      onTouchEnd={onCardTouchEnd}
      ref={cardRef}
    >
      <div
        className={clsx("card-item-content", styles.Card_content)}
        ref={contentRef}
      >
        {isShowFrontSide && (
          <div className={clsx(styles.Card_front)}>
            {!!data?.price && data?.price > 0 && (
              <div className={styles.Card_price}>
                <OutlinedText className={styles.Card_price_text}>
                  {data?.price}
                </OutlinedText>
                {runeImg && <img src={runeImg} alt="stamina" />}
              </div>
            )}
            <div className={styles.Card_front_frame}>
              <img
                src={getCdnAssetUrl(cardSkin.frontImgSrc)}
                alt="card-front"
              />
            </div>
            <div className={styles.Card_front_content}>
              <div
                className={styles.Card_img}
                style={{
                  background: cardSkin.colorBg
                }}
              >
                <img src={getCdnAssetUrl(data?.imgSrc)} alt="card-image" />
              </div>

              <div
                className={styles.Card_front_title}
                style={{ color: cardSkin.colorTitle }}
              >
                <ArcText
                  className={styles.Card_front_title_arc}
                  text={title || ""}
                />
              </div>

              <div
                ref={descriptionRef}
                className={styles.Card_front_description}
                style={{
                  background: cardSkin.colorBg
                }}
              >
                {description &&
                  (USE_SVG_DESCRIPTION ? (
                    <CardDescriptionSvg
                      text={description}
                      color={cardSkin.colorDescription}
                      scale={descriptionScale}
                    />
                  ) : (
                    <p
                      style={{
                        color: cardSkin.colorDescription,
                        ...(fontSize !== undefined && { fontSize }),
                        ...(lineClamp !== undefined && {
                          display: "-webkit-box",
                          WebkitBoxOrient: "vertical",
                          WebkitLineClamp: lineClamp,
                          overflow: "hidden"
                        })
                      }}
                    >
                      {description}
                    </p>
                  ))}
                <CardTextShadowImg color={cardSkin.colorBgShadow} />
              </div>
            </div>
          </div>
        )}
        {isShowBackSide && (
          <div className={clsx(styles.Card_back)}>
            <div className={clsx(styles.Card_back_img)}>
              <img src={getCdnAssetUrl(cardSkin.backImgSrc)} alt="card-back" />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export const CardView = memo(CardViewComponent)
